// ============================================================================
// SCHEDULE GENERATOR
// Generates a balanced 82-game NBA schedule per real league structure
// Division rivals: 4 games each (16 total)
// Same conference other division: 3 or 4 games (36 total)
// Cross conference: 2 games each (30 total)
// Total: 82 games per team
// ============================================================================

const DIVISIONS = {
  Atlantic:  ['BOS', 'BKN', 'NYK', 'PHI', 'TOR'],
  Central:   ['CHI', 'CLE', 'DET', 'IND', 'MIL'],
  Southeast: ['ATL', 'CHA', 'MIA', 'ORL', 'WAS'],
  Northwest: ['DEN', 'MIN', 'OKC', 'POR', 'UTA'],
  Pacific:   ['GSW', 'LAC', 'LAL', 'PHX', 'SAC'],
  Southwest: ['DAL', 'HOU', 'MEM', 'NOP', 'SAS'],
};

const CONFERENCES = {
  East: ['Atlantic', 'Central', 'Southeast'],
  West: ['Northwest', 'Pacific', 'Southwest'],
};

function getTeamDivision(abbr) {
  for (const [div, teams] of Object.entries(DIVISIONS)) {
    if (teams.includes(abbr)) return div;
  }
  return null;
}

function getTeamConference(abbr) {
  const div = getTeamDivision(abbr);
  if (!div) return null;
  return CONFERENCES.East.includes(div) ? 'East' : 'West';
}

function getConferenceTeams(conf) {
  const divs = CONFERENCES[conf];
  return divs.flatMap(d => DIVISIONS[d]);
}

function getAllTeams() {
  return Object.values(DIVISIONS).flat();
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// ============================================================================
// GENERATE MATCHUP LIST (who plays who, how many times)
// ============================================================================
function generateMatchups() {
  const teams = getAllTeams();
  const matchups = {}; // { 'BOS-NYK': { home: 'BOS', away: 'NYK', count: 4 } }

  for (const team of teams) {
    const div = getTeamDivision(team);
    const conf = getTeamConference(team);
    const divTeams = DIVISIONS[div].filter(t => t !== team);
    const confTeams = getConferenceTeams(conf).filter(t => t !== team && !divTeams.includes(t));
    const otherConf = conf === 'East' ? 'West' : 'East';
    const crossTeams = getConferenceTeams(otherConf);

    // Division rivals: 4 games each (2 home, 2 away)
    for (const opp of divTeams) {
      const key = [team, opp].sort().join('-');
      if (!matchups[key]) matchups[key] = { teams: [team, opp].sort(), games: 4 };
    }

    // Same conference, other divisions: mix of 3 and 4
    // 6 teams get 4 games, 4 teams get 3 games → 6*4 + 4*3 = 36
    // (This rotates year to year, we'll randomize)
    const shuffledConf = shuffle(confTeams);
    for (let i = 0; i < confTeams.length; i++) {
      const opp = shuffledConf[i];
      const key = [team, opp].sort().join('-');
      if (!matchups[key]) {
        // First time seeing this matchup - will be set later
        matchups[key] = { teams: [team, opp].sort(), games: 0 };
      }
    }

    // Cross conference: 2 games each (1 home, 1 away)
    for (const opp of crossTeams) {
      const key = [team, opp].sort().join('-');
      if (!matchups[key]) matchups[key] = { teams: [team, opp].sort(), games: 2 };
    }
  }

  // Fix conference matchups to hit 82 games per team
  // Each team needs: 16 (div) + 36 (conf) + 30 (cross) = 82
  // Division and cross already set. Need to assign 3 or 4 to conference games.
  const allTeamsList = getAllTeams();
  for (const team of allTeamsList) {
    const div = getTeamDivision(team);
    const conf = getTeamConference(team);
    const confTeams = getConferenceTeams(conf).filter(t => t !== team && !DIVISIONS[div].includes(t));

    // Count current conference games for this team
    let confGames = 0;
    const confKeys = [];
    for (const opp of confTeams) {
      const key = [team, opp].sort().join('-');
      confKeys.push(key);
      confGames += matchups[key].games;
    }

    // Need 36 conference games total. Assign games to matchups that have 0.
    const unassigned = confKeys.filter(k => matchups[k].games === 0);
    const assigned = confKeys.filter(k => matchups[k].games > 0);
    const assignedTotal = assigned.reduce((s, k) => s + matchups[k].games, 0);
    const needed = 36 - assignedTotal;

    if (unassigned.length > 0 && needed > 0) {
      // Distribute: some get 4, some get 3
      // With 10 conf opponents: need 36 games. 
      // If x get 4 and (10-x) get 3: 4x + 3(10-x) = 36 → x = 6
      const shuffled = shuffle(unassigned);
      const fourGameCount = Math.min(6, shuffled.length);
      for (let i = 0; i < shuffled.length; i++) {
        matchups[shuffled[i]].games = i < fourGameCount ? 4 : 3;
      }
    }
  }

  return matchups;
}

// ============================================================================
// GENERATE FULL SEASON SCHEDULE
// ============================================================================
export function generateSchedule(seed) {
  // Use seed for reproducibility if desired
  if (seed) {
    // Simple seeded shuffle - not cryptographic, just consistent
  }

  const matchups = generateMatchups();
  const allGames = [];

  // For each matchup, create individual games with home/away
  for (const [key, matchup] of Object.entries(matchups)) {
    const [t1, t2] = matchup.teams;
    const total = matchup.games;

    // Split home/away as evenly as possible
    const t1Home = Math.ceil(total / 2);
    const t2Home = total - t1Home;

    for (let i = 0; i < t1Home; i++) {
      allGames.push({ home: t1, away: t2 });
    }
    for (let i = 0; i < t2Home; i++) {
      allGames.push({ home: t2, away: t1 });
    }
  }

  // Shuffle all games to create a season-long schedule
  const shuffled = shuffle(allGames);

  // Assign game numbers (sequential, ~1230 total games)
  // Group into ~82 "game days" (each day has ~15 games)
  const gamesPerDay = 15;
  const schedule = [];
  let gameDay = 1;

  for (let i = 0; i < shuffled.length; i++) {
    if (i > 0 && i % gamesPerDay === 0) gameDay++;
    schedule.push({
      id: i,
      gameDay,
      ...shuffled[i],
      played: false,
      result: null,
    });
  }

  return schedule;
}

// ============================================================================
// GET TEAM SCHEDULE (subset of full schedule for one team)
// ============================================================================
export function getTeamSchedule(schedule, teamAbbr) {
  return schedule
    .filter(g => g.home === teamAbbr || g.away === teamAbbr)
    .map(g => ({
      ...g,
      isHome: g.home === teamAbbr,
      opponent: g.home === teamAbbr ? g.away : g.home,
    }));
}

// ============================================================================
// COMPUTE STANDINGS FROM RESULTS
// ============================================================================
export function computeStandings(schedule) {
  const records = {};

  // Initialize all teams
  for (const teams of Object.values(DIVISIONS)) {
    for (const t of teams) {
      records[t] = { wins: 0, losses: 0, confWins: 0, confLosses: 0, divWins: 0, divLosses: 0, streak: 0, last10: [] };
    }
  }

  // Process played games
  const played = schedule.filter(g => g.played && g.result);
  for (const game of played) {
    const { home, away, result } = game;
    const winner = result.winner;
    const loser = winner === home ? away : home;
    const sameConf = getTeamConference(home) === getTeamConference(away);
    const sameDiv = getTeamDivision(home) === getTeamDivision(away);

    records[winner].wins++;
    records[loser].losses++;
    if (sameConf) { records[winner].confWins++; records[loser].confLosses++; }
    if (sameDiv) { records[winner].divWins++; records[loser].divLosses++; }

    // Last 10 and streak
    records[winner].last10.push('W');
    records[loser].last10.push('L');
    if (records[winner].last10.length > 10) records[winner].last10.shift();
    if (records[loser].last10.length > 10) records[loser].last10.shift();
  }

  // Compute streak
  for (const [team, rec] of Object.entries(records)) {
    const games = schedule.filter(g => g.played && g.result && (g.home === team || g.away === team));
    let streak = 0;
    let streakType = '';
    for (let i = games.length - 1; i >= 0; i--) {
      const w = games[i].result.winner === team;
      const type = w ? 'W' : 'L';
      if (streakType === '') streakType = type;
      if (type === streakType) streak++;
      else break;
    }
    rec.streak = streakType === 'W' ? streak : -streak;
    rec.pct = rec.wins + rec.losses > 0 ? rec.wins / (rec.wins + rec.losses) : 0;
    rec.gamesPlayed = rec.wins + rec.losses;
  }

  // Sort by conference
  const byConf = (conf) => {
    const teams = getConferenceTeams(conf);
    return teams
      .map(t => ({ abbr: t, ...records[t], conference: conf, division: getTeamDivision(t) }))
      .sort((a, b) => b.pct - a.pct || b.wins - a.wins);
  };

  return {
    east: byConf('East'),
    west: byConf('West'),
    records,
  };
}

// ============================================================================
// PLAYOFF SEEDING
// ============================================================================
export function getPlayoffSeeds(standings) {
  const seed = (confStandings) => {
    // Top 6 get in directly, 7-10 go to play-in
    return {
      playoff: confStandings.slice(0, 6).map((t, i) => ({ ...t, seed: i + 1 })),
      playIn: confStandings.slice(6, 10).map((t, i) => ({ ...t, seed: i + 7 })),
      lottery: confStandings.slice(10),
    };
  };

  return {
    east: seed(standings.east),
    west: seed(standings.west),
  };
}

export default { generateSchedule, getTeamSchedule, computeStandings, getPlayoffSeeds };
